(() => {
  'use strict';

  if (window.themeAnalytics) return;

  const SECTION_SELECTOR = '[data-analytics-section]';
  const ITEM_SELECTOR = 'product-block[data-analytics-item-id]';
  const MIN_VISIBLE_DURATION = 1000;
  const MAX_ITEMS_PER_EVENT = 200;
  const COMPLETE_THE_LOOK_EVENT = 'complete-the-look:track';
  const COMPLETE_THE_LOOK_ATTRIBUTION_KEY = 'complete_the_look_attribution';
  const COMPLETE_THE_LOOK_ATTRIBUTION_TTL = 30 * 60 * 1000;
  const HEADER_NAV_SELECTOR = '.navigation__link[data-nav-item-id]';
  const HEADER_NAV_ATTRIBUTION_KEY = 'theme_analytics_navigation_source';
  const MODULE_SOURCE_KEY = 'theme_analytics_source';
  const MODULE_SOURCE_TTL = 30 * 60 * 1000;
  const sectionStates = new WeakMap();
  const observedSections = new WeakSet();
  const trackedItemListItems = new WeakMap();
  let observer;
  let contentObserver;

  const config = window.themeAnalyticsConfig || {};

  function hasAnalyticsConsent() {
    if (!config.requireAnalyticsConsent) return true;

    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;

    try {
      return privacy.analyticsProcessingAllowed();
    } catch (error) {
      return false;
    }
  }

  function loadConsentTrackingApi() {
    if (!config.requireAnalyticsConsent) return;
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function' || window.Shopify.customerPrivacy) return;

    window.Shopify.loadFeatures([{
      name: 'consent-tracking-api',
      version: '0.1',
      onLoad: (error) => {
        if (!error) start();
      }
    }]);
  }

  function send(eventName, params) {
    if (!hasAnalyticsConsent()) return false;

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'ggradual_ga4_event',
        ggradual_event_name: eventName,
        ggradual_event_params: Object.assign({
          page_type: config.pageType || '',
          page_path: window.location.pathname,
          currency: config.currency || undefined,
          debug_mode: new URLSearchParams(window.location.search).get('ga_debug') === '1' || undefined,
          tracking_source: 'theme_gtm'
        }, params)
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  function isCompleteTheLook(section) {
    return section && section.dataset.analyticsSectionType === 'complete-the-look';
  }

  function completeTheLookAttribution() {
    try {
      const attribution = JSON.parse(sessionStorage.getItem(COMPLETE_THE_LOOK_ATTRIBUTION_KEY));
      if (!attribution || Date.now() - attribution.timestamp > COMPLETE_THE_LOOK_ATTRIBUTION_TTL) {
        sessionStorage.removeItem(COMPLETE_THE_LOOK_ATTRIBUTION_KEY);
        return null;
      }
      return attribution;
    } catch (error) {
      return null;
    }
  }

  function trackCompleteTheLook(event) {
    const detail = event.detail || {};
    if (!detail.eventName || !detail.params) return;
    send(detail.eventName, detail.params);
  }

  function trackCompleteTheLookAddToCart(event) {
    const target = event.target;
    if (!target || target.closest('complete-the-look') || !target.closest('product-form')) return;

    const attribution = completeTheLookAttribution();
    if (!attribution || attribution.recommendation_path !== window.location.pathname) return;

    const detail = event.detail || {};
    const price = Number(detail.price);
    const quantity = Number(detail.quantity) || 1;
    try {
      sessionStorage.setItem(COMPLETE_THE_LOOK_ATTRIBUTION_KEY, JSON.stringify(Object.assign({}, attribution, {
        added: true,
        quantity,
        price: Number.isFinite(price) ? price : undefined
      })));
    } catch (error) {
      // Attribution storage is optional and must not affect cart updates.
    }
    send('complete_the_look_add_to_cart', {
      main_product_id: attribution.main_product_id,
      recommendation_product_id: attribution.recommendation_product_id,
      quantity,
      price: Number.isFinite(price) ? price : undefined,
      recommendation_position: attribution.recommendation_position
    });
  }

  function trackCompleteTheLookCheckout(event) {
    const control = event.target && event.target.closest('button[name="checkout"], a[href*="/checkout"]');
    if (!control || control.dataset.completeTheLookCheckoutTracked === 'true') return;

    const attribution = completeTheLookAttribution();
    if (!attribution || !attribution.added) return;

    control.dataset.completeTheLookCheckoutTracked = 'true';
    send('complete_the_look_begin_checkout', {
      main_product_id: attribution.main_product_id,
      recommendation_product_id: attribution.recommendation_product_id,
      quantity: attribution.quantity || 1,
      price: attribution.price,
      recommendation_position: attribution.recommendation_position
    });
  }

  function text(value) {
    return (value || '').replace(/\s+/g, ' ').trim().slice(0, 100);
  }

  function isAnalyticsHidden(el) {
    if (!el) return true;
    if (el.hidden || (typeof el.matches === 'function' && el.matches('[hidden]'))) return true;
    return typeof el.closest === 'function' && !!el.closest('[hidden]');
  }

  function visibleItemNodes(section) {
    return Array.from(section.querySelectorAll(ITEM_SELECTOR)).filter((item) => !isAnalyticsHidden(item));
  }

  function storeModuleSource(params) {
    try {
      sessionStorage.setItem(MODULE_SOURCE_KEY, JSON.stringify(Object.assign({}, params, {
        timestamp: Date.now(),
        ttl_ms: MODULE_SOURCE_TTL
      })));
    } catch (error) {
      // Source attribution is optional and must not affect navigation.
    }
  }

  function searchTerm(value) {
    const term = text(value);
    return term && !/@|(?:\d[\s().-]*){7,}/.test(term) ? term : '';
  }

  function sectionMeta(section) {
    const sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    const position = sections.indexOf(section) + 1;

    return {
      section_id: section.dataset.analyticsSectionId || section.id || '',
      section_type: section.dataset.analyticsSectionType || '',
      section_name: section.dataset.analyticsSectionName || section.dataset.analyticsSectionType || '',
      section_position: position > 0 ? position : undefined
    };
  }

  function getItemPayload(item, index) {
    const price = Number(item.dataset.analyticsItemPrice);
    const payload = {
      item_id: item.dataset.analyticsItemId,
      item_name: item.dataset.analyticsItemName,
      item_variant: item.dataset.analyticsItemVariant || undefined,
      item_category: item.dataset.analyticsItemCategory || undefined,
      index: index + 1
    };

    if (Number.isFinite(price)) payload.price = price;
    return payload;
  }

  function getItems(section, itemNodes, allItems) {
    const sectionItems = allItems || Array.from(section.querySelectorAll(ITEM_SELECTOR));
    const nodes = itemNodes || sectionItems;

    return nodes.map((item) => {
      const index = sectionItems.indexOf(item);
      return index >= 0 ? getItemPayload(item, index) : null;
    }).filter((item) => item && item.item_id && item.item_name);
  }

  function analyticsSelfCheck() {
    const item = {
      dataset: {
        analyticsItemId: 'self-check',
        analyticsItemName: 'self-check',
        analyticsItemPrice: '1'
      }
    };
    const valid = getItemPayload(item, 20).index === 21;

    console.assert(valid, 'collection item position tracking');
    return valid;
  }

  function headerNavigationSelfCheck() {
    const link = document.createElement('a');
    link.dataset.navItemId = 'self-check';
    link.dataset.navItemName = 'Self Check';
    link.dataset.navLevel = '2';
    link.dataset.navParentId = 'parent';
    link.dataset.navPath = 'Parent > Self Check';
    link.dataset.navDevice = 'desktop';
    link.dataset.navPosition = '2';
    const valid = link.dataset.navLevel === '2'
      && link.dataset.navPath === 'Parent > Self Check'
      && ['desktop', 'mobile'].includes(link.dataset.navDevice);

    console.assert(valid, 'header navigation metadata');
    return valid;
  }

  function sendItemList(section) {
    const state = sectionStates.get(section);
    // A dynamically rendered list must not report an impression before its
    // parent section has actually entered the viewport.
    if (!state || !state.viewSent) return;

    const allItems = visibleItemNodes(section);
    if (!allItems.length) return;

    let trackedItems = trackedItemListItems.get(section);
    if (!trackedItems) {
      trackedItems = new WeakSet();
      trackedItemListItems.set(section, trackedItems);
    }

    const newItems = allItems.filter((item) => !trackedItems.has(item));

    for (let offset = 0; offset < newItems.length; offset += MAX_ITEMS_PER_EVENT) {
      const batchNodes = newItems.slice(offset, offset + MAX_ITEMS_PER_EVENT);
      const validNodes = batchNodes.filter((item) => (
        item.dataset.analyticsItemId && item.dataset.analyticsItemName
      ));
      const items = getItems(section, validNodes, allItems);
      if (!items.length) continue;

      const meta = sectionMeta(section);
      if (!send('view_item_list', Object.assign({}, meta, {
        item_list_id: meta.section_id,
        item_list_name: meta.section_name,
        items
      }))) {
        return;
      }

      validNodes.forEach((item) => trackedItems.add(item));
    }
  }

  function pause(section) {
    const state = sectionStates.get(section);
    if (!state || state.startedAt === null) return;

    state.visibleDuration += performance.now() - state.startedAt;
    state.startedAt = null;
  }

  function resume(section) {
    const state = sectionStates.get(section);
    if (!state || state.startedAt !== null || document.hidden || !state.viewSent || !state.isVisible) return;

    state.startedAt = performance.now();
  }

  function flushEngagement(section) {
    const state = sectionStates.get(section);
    if (!state || state.engagementSent || !state.viewSent) return;

    pause(section);
    const visibleDuration = Math.round(state.visibleDuration);
    if (visibleDuration < MIN_VISIBLE_DURATION) return;

    if (send('section_engagement', Object.assign({}, sectionMeta(section), {
      visible_duration_ms: visibleDuration
    }))) {
      state.engagementSent = true;
    }
  }

  function observeSection(section) {
    if (observedSections.has(section)) return;

    observedSections.add(section);
    sectionStates.set(section, {
      startedAt: null,
      visibleDuration: 0,
      viewSent: false,
      engagementSent: false,
      isVisible: false
    });
    observer.observe(section);
  }

  function initialize(root) {
    if (!observer || !root) return;

    const sections = [];
    if (root.matches && root.matches(SECTION_SELECTOR)) sections.push(root);
    if (root.querySelectorAll) sections.push(...root.querySelectorAll(SECTION_SELECTOR));

    sections.forEach((section) => {
      if (isCompleteTheLook(section)) return;
      if (!observedSections.has(section)) {
        observeSection(section);
        return;
      }

      const state = sectionStates.get(section);
      if (state && !state.viewSent) {
        observer.unobserve(section);
        observer.observe(section);
      }
    });
  }

  function trackClick(event) {
    if (!event.target || typeof event.target.closest !== 'function') return;

    const control = event.target.closest('a, button, [role="button"]');
    if (!control || isAnalyticsHidden(control)) return;

    const section = control.closest(SECTION_SELECTOR);
    if (!section) return;
    if (isCompleteTheLook(section)) return;

    const meta = sectionMeta(section);
    const productBlock = control.closest(ITEM_SELECTOR);
    const label = control.dataset.analyticsLabel || control.getAttribute('aria-label') || text(control.textContent);

    if (control.matches('[data-collection-infinite-scroll]')) return;

    if (control.closest('.amazon-button-block')) {
      send('amazon_click', Object.assign({}, meta, {
        element_label: label || 'Buy on Amazon',
        destination_url: control.href || ''
      }));
      return;
    }

    send('section_click', Object.assign({}, meta, {
      element_type: control.tagName.toLowerCase(),
      element_label: label || undefined,
      destination_url: control.href || undefined
    }));
    storeModuleSource({
      section_id: meta.section_id,
      section_name: meta.section_name,
      section_type: meta.section_type,
      destination_url: control.href || undefined
    });

    if (!productBlock || isAnalyticsHidden(productBlock)) return;

    const allItems = visibleItemNodes(section);
    const itemIndex = allItems.indexOf(productBlock);
    const selectedItem = itemIndex >= 0
      ? getItemPayload(productBlock, itemIndex)
      : null;
    if (!selectedItem) return;

    const afterLoadMore = section.dataset.afterLoadMore === 'true';
    const loadMoreClickNumber = Number(section.dataset.loadMoreClickNumber);
    const itemSelected = send('select_item', Object.assign({}, meta, {
      item_list_id: meta.section_id,
      item_list_name: meta.section_name,
      after_load_more: afterLoadMore ? 'true' : 'false',
      load_more_click_number: afterLoadMore && Number.isFinite(loadMoreClickNumber)
        ? String(loadMoreClickNumber)
        : undefined,
      items: [selectedItem]
    }));

    if (!itemSelected) return;

    storeModuleSource({
      section_id: meta.section_id,
      section_name: meta.section_name,
      section_type: meta.section_type,
      item_id: selectedItem.item_id,
      destination_url: control.href || undefined
    });
  }

  function trackHeaderNavigation(event) {
    if (!event.target || typeof event.target.closest !== 'function') return;

    const control = event.target.closest(HEADER_NAV_SELECTOR);
    if (!control || !control.closest('main-navigation, #proxy-nav')) return;

    const style = window.getComputedStyle(control);
    if (style.display === 'none' || style.visibility === 'hidden' || !control.getClientRects().length) return;

    const params = {
      nav_item_id: control.dataset.navItemId,
      nav_item_name: control.dataset.navItemName || text(control.textContent),
      nav_level: control.dataset.navLevel,
      nav_parent_id: control.dataset.navParentId || undefined,
      nav_path: control.dataset.navPath || undefined,
      nav_device: control.dataset.navDevice || (window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop'),
      nav_position: control.dataset.navPosition,
      destination_url: control.href || control.dataset.navDestinationUrl || undefined
    };

    if (!send('header_navigation_click', params)) return;

    try {
      sessionStorage.setItem(HEADER_NAV_ATTRIBUTION_KEY, JSON.stringify(Object.assign({}, params, {
        timestamp: Date.now()
      })));
    } catch (error) {
      // Attribution storage is optional and must not affect navigation.
    }
  }

  function trackHelpSearch(event) {
    const form = event.target;
    if (!form || !form.matches || !form.matches('form[data-analytics-help-search]')) return;

    const term = searchTerm(form.elements && form.elements.q && form.elements.q.value);
    if (!term) return;

    send('search', {
      search_term: term,
      search_context: form.dataset.analyticsSearchContext || 'help_center'
    });
  }

  function searchSelfCheck() {
    const valid = searchTerm(' return policy ') === 'return policy';
    const email = searchTerm('help@example.com') === '';
    const number = searchTerm('1234567') === '';
    console.assert(valid, 'normal search term');
    console.assert(email, 'email not tracked');
    console.assert(number, 'long number not tracked');
    return valid && email && number;
  }

  function start() {
    if (!observer) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const section = entry.target;
          const state = sectionStates.get(section);
          if (!state) return;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            state.isVisible = true;
            if (!state.viewSent) {
              state.viewSent = send('section_view', sectionMeta(section));
              if (state.viewSent) sendItemList(section);
            }
            if (state.viewSent) {
              resume(section);
            }
          } else {
            state.isVisible = false;
            pause(section);
            // Flush engagement as soon as a visitor leaves the module. This
            // makes the dwell-time event useful even when the page is not
            // unloaded (for example, during normal scrolling).
            flushEngagement(section);
          }
        });
      }, { threshold: [0, 0.5] });
    }

    initialize(document);
  }

  function watchDynamicContent() {
    if (contentObserver || !document.body) return;

    contentObserver = new MutationObserver((records) => {
      const changedSections = new Set();

      records.forEach((record) => {
        if (record.type === 'attributes') {
          const target = record.target;
          if (!target || target.nodeType !== Node.ELEMENT_NODE) return;
          const parentSection = target.closest && target.closest(SECTION_SELECTOR);
          if (parentSection) changedSections.add(parentSection);
          return;
        }

        record.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          initialize(node);

          const parentSection = node.closest && node.closest(SECTION_SELECTOR);
          if (parentSection) changedSections.add(parentSection);
        });
      });

      changedSections.forEach(sendItemList);
    });
    contentObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden']
    });
  }

  document.addEventListener('click', trackClick, true);
  document.addEventListener('click', trackHeaderNavigation, true);
  document.addEventListener('click', trackCompleteTheLookCheckout, true);
  document.addEventListener('on:cart:add', trackCompleteTheLookAddToCart);
  document.addEventListener(COMPLETE_THE_LOOK_EVENT, trackCompleteTheLook);
  document.addEventListener('submit', trackHelpSearch, true);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target && event.target.matches && event.target.matches(SECTION_SELECTOR)) {
      flushEngagement(event.target);
      observer && observer.unobserve(event.target);
    }
  });
  document.addEventListener('visitorConsentCollected', start);
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (document.hidden) {
        pause(section);
      } else {
        resume(section);
      }
    });
  });
  window.addEventListener('pagehide', () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach(flushEngagement);
  });

  function boot() {
    start();
    watchDynamicContent();
    loadConsentTrackingApi();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  window.addEventListener('load', boot, { once: true });

  if (new URLSearchParams(window.location.search).get('ga_debug') === '1') {
    analyticsSelfCheck();
    headerNavigationSelfCheck();
  }

  (window.completeTheLookAnalyticsQueue || []).splice(0).forEach(trackCompleteTheLook);

  window.themeAnalytics = {
    initialize,
    send,
    sendItemList,
    searchSelfCheck,
    headerNavigationSelfCheck,
    isAnalyticsHidden,
    visibleItemNodes
  };
})();
